import * as ApolloReactHooks from '@apollo/react-hooks'
import { AutocompleteField } from '@forgeglobal/design-framework'
import { FieldValidator } from 'final-form'
import { OperationVariables, QueryResult } from 'react-apollo'
import { useField } from 'react-final-form'
import React, { useState } from 'react'

export type OperationVars = { query: string } & OperationVariables

export interface AsyncAutocompleteFieldType {
  name: string
  label: string
  freeSolo?: boolean
  multiple?: boolean
  validate?: FieldValidator<any>
}

export interface HookActionsType<TData, TVariables extends OperationVars, MenuOptionType> {
  useQuery: (baseOptions?: ApolloReactHooks.QueryHookOptions<TData, TVariables>) => QueryResult<TData, TVariables>
  transform: (data: TData) => { label: string; value: MenuOptionType }[]
}

export type HookAutocompleteFieldProps<TData, TVariables extends OperationVars, MenuOptionType> = HookActionsType<
  TData,
  TVariables,
  MenuOptionType
> &
  AsyncAutocompleteFieldType

export function HookAutocompleteField<TData, TVariables extends OperationVars, MenuOptionType>({
  useQuery,
  name,
  label,
  freeSolo,
  multiple,
  validate,
  transform,
}: HookAutocompleteFieldProps<TData, TVariables, MenuOptionType>): React.ReactElement<
  HookAutocompleteFieldProps<TData, TVariables, MenuOptionType>
> {
  const renderProps = useField(name, { validate: validate })

  const [touched, setTouched] = useState(false)
  const { data, refetch } = useQuery({ variables: { query: '' } as any, fetchPolicy: 'network-only', skip: !touched })
  const options = data ? transform(data) : []
  const onInputChange = (value: any) => {
    setTouched(true)
    refetch({ query: value as string } as any)
  }
  return (
    <AutocompleteField
      options={options}
      label={label}
      onInputChange={onInputChange}
      freeSolo={freeSolo}
      multiple={multiple}
      not_touched={!touched || renderProps.meta.pristine}
      {...renderProps}
    />
  )
}
